import React, { useState, useEffect } from 'react';
import { Calendar, MapPin, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const EventsPage = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await fetch('https://atabackend.quindl.com/events/get');
        if (!response.ok) {
          throw new Error('Failed to fetch events');
        }
        const data = await response.json();
        const activeEvents = data.filter(event => event.status === "Active");
        setEvents(activeEvents);
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEvents();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % 3); // Assuming max 3 images per event
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    if (!timeString || timeString === 'undefined') return '';
    const [hours, minutes] = timeString.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true
    });
  };

  const getDaysUntil = (dateString) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const eventDate = new Date(dateString);
    eventDate.setHours(0, 0, 0, 0);
    const diffTime = eventDate - today;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

const categorizeEvents = () => {
  const ongoingEvents = [];
  const upcomingEvents = [];

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  events.forEach(eventItem => {
    const eventStartDate = new Date(eventItem.date);
    eventStartDate.setHours(0, 0, 0, 0);
    const eventEndDate = eventItem.endDate ? new Date(eventItem.endDate) : null;
    if (eventEndDate) eventEndDate.setHours(0, 0, 0, 0);

    // Ongoing Events: Events that have started or are in progress
    const isOngoing = 
      (eventStartDate <= today && (!eventEndDate || eventEndDate >= today));

    // Upcoming Events: Events starting after today
    const isUpcoming = eventStartDate > today;

    if (isOngoing) {
      ongoingEvents.push(eventItem);
    } else if (isUpcoming) {
      upcomingEvents.push({
        ...eventItem,
        daysUntil: getDaysUntil(eventItem.date)
      });
    }
  });

  // Sort events by date
  ongoingEvents.sort((a, b) => new Date(a.date) - new Date(b.date));
  upcomingEvents.sort((a, b) => new Date(a.date) - new Date(b.date));

  return { ongoingEvents, upcomingEvents };
};

  const handleJoinClick = (eventItem) => {
    if (eventItem.eventLink) {
      const link = eventItem.eventLink.startsWith('http://') || eventItem.eventLink.startsWith('https://')
        ? eventItem.eventLink
        : `https://${eventItem.eventLink}`;
      window.open(link, '_blank', 'noopener,noreferrer');
    } else {
      navigate('/membership');
    }
  };

  const EventCard = ({ event: eventItem }) => (
    console.log(eventItem),
    <div className="bg-white rounded-lg shadow-md p-6 min-h-72 w-80 mt-6 flex flex-col justify-between">
      <div className="relative h-32 mb-4">
        {eventItem.images && eventItem.images.length > 0 ? (
          <img 
            src={eventItem.images[currentImageIndex % eventItem.images.length]} 
            alt={eventItem.name} 
            className="w-full h-full object-cover rounded-lg"
          />
        ) : (
          <div className="w-full h-full bg-gray-200 rounded-lg flex items-center justify-center">
            <span className="text-gray-500">No image available</span>
          </div>
        )}
      </div>
      <div className="space-y-3 overflow-hidden">
        <h3 className="text-xl font-semibold text-gray-900 line-clamp-2">
          {eventItem.name}
          {eventItem.daysUntil > 0 && (
            <span className="text-sm font-normal text-gray-500 ml-2">
              {eventItem.daysUntil === 1 ? 'Tomorrow' : `in ${eventItem.daysUntil} days`}
            </span>
          )}
        </h3>

        {/* description */}
        <p className="text-gray-600 line-clamp-2 mt-2">{eventItem.description}</p>

        <div className="flex items-center text-gray-600">
          <Calendar className="w-5 h-5 mr-2 flex-shrink-0" />
          <span className="truncate">
            {formatDate(eventItem.date)}
            {eventItem.endDate && ` - ${formatDate(eventItem.endDate)}`}
          </span>
        </div>

        <div className="flex items-center text-gray-600">
          <Clock className="w-5 h-5 mr-2 flex-shrink-0" />
          <span className="truncate">
            {formatTime(eventItem.time)}
            {eventItem.endTime && ` - ${formatTime(eventItem.endTime)}`}
          </span>
        </div>

        {eventItem.location && (
          <div className="flex items-center text-gray-600">
            <MapPin className="w-5 h-5 mr-2 flex-shrink-0" />
            <span className="truncate">{eventItem.location}</span>
          </div>
        )}

        {eventItem.description && (
          <p className="text-gray-600 line-clamp-2 mt-2">{eventItem.description}</p>
        )}
      </div>

      <button 
        onClick={() => handleJoinClick(eventItem)}
        className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-full transition-colors duration-200 w-full mt-4"
      >
        {eventItem.eventLink ? 'Purchase Tickets ' : 'Register'}
      </button>
    </div>
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-2xl text-gray-600">Loading events...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-2xl text-red-600">Error: {error}</div>
      </div>
    );
  }

  const { ongoingEvents, upcomingEvents } = categorizeEvents();

  const headingStyle = "relative text-3xl font-bold pb-2 mb-6 text-gray-800 after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-20 after:h-1 after:bg-orange-500";

  return (
    <div className="min-h-screen bg-gray-50 p-6 mt-6">
      <div className="max-w-7xl mx-auto">
        <h1 className={headingStyle + " text-4xl after:w-32"}>EVENTS</h1>
        
        {ongoingEvents.length > 0 ? (
          <div className="mt-8">
            <h2 className="font-bold text-2xl">Ongoing Events</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {ongoingEvents.map((eventItem) => (
                <EventCard 
                  key={eventItem._id || `event-${eventItem.name}-${eventItem.date}`} 
                  event={eventItem} 
                />
              ))}
            </div>
          </div>
        ) : null}

        {upcomingEvents.length > 0 ? (
          <div className="mt-12">
            <h2 className="font-bold text-2xl">Upcoming Events</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {upcomingEvents.map((eventItem) => (
                <EventCard 
                  key={eventItem._id || `event-${eventItem.name}-${eventItem.date}`} 
                  event={eventItem} 
                />
              ))}
            </div>
          </div>
        ) : null}

        {ongoingEvents.length === 0 && upcomingEvents.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-600 mt-4">No events scheduled at this time.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventsPage;